import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  FormControl,
  HStack,
  Switch,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { ReactElement } from "react-markdown/lib/react-markdown";

import { Button, LoadingIndicator } from "../../../../components";

interface SettingSwitchWithConfirmDialogueProps {
  isChecked: boolean;
  children: React.ReactNode;
  shouldConfirm?: boolean;
  headerText?: string;
  headerBody?: string | ReactElement;
  cancelButtonText?: string;
  confirmButtonText?: string;
  onToggle: (checked: boolean) => Promise<any>;
}

export const SettingSwitchWithConfirmDialogue: React.FC<
  SettingSwitchWithConfirmDialogueProps
> = ({
  isChecked,
  children,
  headerText = "Please confirm",
  headerBody = "Are you sure you wish to proceed?",
  cancelButtonText = "Cancel",
  confirmButtonText = "Confirm",
  shouldConfirm = true,
  onToggle,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.createRef<HTMLButtonElement>();
  const [loading, setLoading] = useState(false);

  const handleConfirm = (checked: boolean): void => {
    onClose();
    setLoading(true);
    onToggle(checked)
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (shouldConfirm) {
      onOpen();
      return;
    }

    setLoading(true);
    onToggle(e.target.checked)
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <HStack align="baseline" spacing="3" maxW="120px">
        {loading ? (
          <LoadingIndicator size="xs" alignSelf="flex-start" width="inherit" />
        ) : (
          <FormControl>
            <Switch
              colorScheme="prism"
              display="flex"
              alignItems="center"
              fontSize="sm"
              lineHeight={1.5}
              isChecked={isChecked}
              onChange={handleChange}
            >
              {children}
            </Switch>
          </FormControl>
        )}
      </HStack>

      <AlertDialog
        motionPreset="slideInRight"
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {headerText}
            </AlertDialogHeader>
            <AlertDialogCloseButton />

            <AlertDialogBody>{headerBody}</AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                {cancelButtonText}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  handleConfirm(!isChecked);
                }}
                ml={3}
              >
                {confirmButtonText}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
